import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { getSupabaseBrowserClient } from '@/lib/supabase';
import { clearClientSessionState } from '@/lib/client-session-clear';

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  /** False when VITE_SUPABASE_* is missing — OAuth buttons stay disabled. */
  authConfigured: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const supabase = getSupabaseBrowserClient();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) {
      setSession(null);
      setLoading(false);
      return;
    }

    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase]);

  const signOut = useCallback(async () => {
    // RECEPTION-MONEY-TRUTH-V1 (B4 / W-1): clear the app's OWN cached
    // business data FIRST -- shared front-desk machines must render the
    // signed-out screen with nothing behind it even if the network
    // sign-out below fails. supabase.auth.signOut() then removes its own
    // sb-* token and flips the session (the auth listener re-renders the
    // signed-out screen).
    clearClientSessionState();
    if (!supabase) return;
    await supabase.auth.signOut();
  }, [supabase]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      authConfigured: supabase !== null,
      signOut,
    }),
    [session, loading, supabase, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
